/* iter97 — Unscheduled Work rail rendered as a sticky "8th column" on the Schedule page.
   Aggregates 3 sources (customer SRs pending, PM SRs Submitted, manual drafts) into one list.
   Cards are HTML5-draggable (data type 'application/x-unscheduled-item') so dispatchers can
   drop them onto any tech/date cell to schedule. They can also click "Schedule…" or "+ Add Draft". */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Plus, Building2, Phone, Siren, Clock, CalendarPlus, ExternalLink, Trash2, RefreshCw, PanelRightClose, PanelRightOpen, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import ReturnVisitCard from './schedule/ReturnVisitCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRIORITY_STYLE = {
  Emergency: 'border-l-[#EF4444] bg-[#7F1D1D]/10',
  Urgent:    'border-l-[#F59E0B] bg-[#7C2D12]/10',
  Normal:    'border-l-[#262626] bg-[#141414]',
};

const SOURCE_LABEL = {
  customer_sr: 'CUSTOMER',
  pm_sr:       'PM PORTAL',
  draft:       'DRAFT',
};

const ageLabel = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '';
  const hrs = Math.floor(ms / 36e5);
  if (hrs < 1) return 'just now';
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const isAging = (iso) => {
  if (!iso) return false;
  return Date.now() - new Date(iso).getTime() > 86_400_000;
};

const UnscheduledRail = ({ employees, onScheduled, onOpenSource, collapsed = false, onToggleCollapsed, onShiftDroppedToUnscheduled, onAddUnscheduled, onEditUnscheduled, onAddReturnVisit, initialFilter }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(initialFilter || 'all');
  const [addDraftOpen, setAddDraftOpen] = useState(false);
  const [scheduleItem, setScheduleItem] = useState(null);  // the item we're scheduling via modal
  // iter128 — Highlight the rail when a scheduled shift is being dragged over it.
  const [shiftDragOver, setShiftDragOver] = useState(false);
  // iter292 — Track an in-flight drag so a quiet background refresh never
  // yanks the card out from under the dispatcher mid-drag.
  const draggingRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  // iter292 — `silent` background refreshes reconcile against canonical server
  // state WITHOUT flipping the loading spinner (no per-cycle flashing) and are
  // skipped while a mutation is active (dragging a card, or a schedule/add
  // dialog is open) so polling can't overwrite the dispatcher's action.
  const load = async (opts = {}) => {
    const silent = !!opts.silent;
    if (silent && (draggingRef.current || scheduleItem || addDraftOpen)) return;
    if (!silent) setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/unscheduled-work`, { withCredentials: true });
      if (!mountedRef.current) return;
      // Full replace = reconcile by canonical id: new→add, updated→update,
      // no-longer-unscheduled→remove, no duplicates (rows keyed by source-id).
      setItems(data || []);
    } catch (err) {
      console.error(err);
      if (!silent) toast.error('Failed to load unscheduled work');
    }
    if (!silent) setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Expose load on window so the parent can refresh after drag-drop scheduling
  // (forced, with spinner) and on every canonical auto-refresh tick (silent).
  useEffect(() => {
    window.__nexflow_unscheduled_reload = load;
    window.__nexflow_unscheduled_reload_silent = () => load({ silent: true });
    return () => {
      delete window.__nexflow_unscheduled_reload;
      delete window.__nexflow_unscheduled_reload_silent;
    };
  });

  const filtered = useMemo(() => {
    if (filter === 'all') return items;
    if (filter === 'aging') return items.filter((i) => isAging(i.created_at));
    if (filter === 'Emergency') return items.filter((i) => i.priority === 'Emergency');
    return items.filter((i) => i.source === filter);
  }, [items, filter]);

  const counts = useMemo(() => ({
    all:         items.length,
    customer_sr: items.filter((i) => i.source === 'customer_sr').length,
    pm_sr:       items.filter((i) => i.source === 'pm_sr').length,
    draft:       items.filter((i) => i.source === 'draft').length,
    return_visit: items.filter((i) => i.source === 'return_visit').length,
    Emergency:   items.filter((i) => i.priority === 'Emergency').length,
    aging:       items.filter((i) => isAging(i.created_at)).length,
    // iter111 — Portal-sourced requests count = anything we did NOT enter as a draft.
    // These are NEW signals from PM Portal / Customer Portal that dispatch must triage.
    portalNew:   items.filter((i) => i.source === 'customer_sr' || i.source === 'pm_sr').length,
  }), [items]);

  const handleDragStart = (e, item) => {
    draggingRef.current = true;
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('application/x-unscheduled-item', JSON.stringify(item));
    // Also a plain text fallback so default DnD targets don't bork.
    e.dataTransfer.setData('text/plain', `unsched:${item.source}:${item.id}`);
  };

  const handleDeleteDraft = async (id) => {
    if (!window.confirm('Delete this draft?')) return;
    try {
      await axios.delete(`${API}/admin/unscheduled-drafts/${id}`, { withCredentials: true });
      toast.success('Draft removed');
      load();
    } catch (e) { toast.error('Delete failed'); }
  };

  const FilterChip = ({ value, label, count }) => (
    <button
      data-testid={`unsched-filter-${value}`}
      onClick={() => setFilter(value)}
      className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border transition-colors ${
        filter === value
          ? 'bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37]'
          : 'bg-transparent text-[#A3A3A3] border-[#262626] hover:border-[#3f3f3f] hover:text-white'
      }`}
    >
      {label} <span className="opacity-70">({count})</span>
    </button>
  );

  return (
    <div
      data-testid="unscheduled-rail"
      data-collapsed={collapsed ? '1' : '0'}
      data-shift-dragover={shiftDragOver ? '1' : '0'}
      className={`${collapsed ? 'w-12' : 'w-[300px]'} shrink-0 border-l ${shiftDragOver ? 'border-[#D4AF37] ring-2 ring-[#D4AF37]/40' : 'border-[#262626]'} bg-[#0A0A0A] flex flex-col max-h-[calc(100vh-180px)] transition-[width,box-shadow] duration-150`}
      onDragOver={(e) => {
        // iter128 — Accept scheduled-shift drops only (text/plain shift ids). Ignore the
        // rail's own card dragstart events (those send application/x-unscheduled-item too,
        // and we don't want a card dropped on itself to trigger a move-to-unscheduled).
        const types = Array.from(e.dataTransfer.types || []);
        const isUnschedCard = types.includes('application/x-unscheduled-item');
        const isShift = types.includes('text/plain') && !isUnschedCard;
        if (isShift && typeof onShiftDroppedToUnscheduled === 'function') {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          if (!shiftDragOver) setShiftDragOver(true);
        }
      }}
      onDragLeave={(e) => {
        // Only clear when the drag actually leaves the rail (not when entering a child).
        if (!e.currentTarget.contains(e.relatedTarget)) setShiftDragOver(false);
      }}
      onDrop={(e) => {
        setShiftDragOver(false);
        const types = Array.from(e.dataTransfer.types || []);
        if (types.includes('application/x-unscheduled-item')) return; // ignore self-drops
        const shiftId = (e.dataTransfer.getData('text/plain') || '').trim();
        if (!shiftId) return;
        e.preventDefault();
        if (typeof onShiftDroppedToUnscheduled === 'function') onShiftDroppedToUnscheduled(shiftId);
      }}
    >
      {/* iter111 — Collapsed strip is wider (48px) with a gold accent border, vertical
          "UNSCHEDULED · n" label, plus a red NEW badge when there are portal-sourced
          requests (customer/PM portal) waiting in the holding area. Strip is clickable
          anywhere to expand. */}
      {collapsed ? (
        <button
          data-testid="unsched-rail-expand"
          onClick={() => onToggleCollapsed?.(false)}
          title={`Unscheduled (${counts.all})${counts.portalNew > 0 ? ` — ${counts.portalNew} new portal request${counts.portalNew === 1 ? '' : 's'}` : ''}`}
          className={`flex-1 flex flex-col items-center justify-start py-3 gap-2 transition-colors relative ${
            counts.portalNew > 0
              ? 'bg-gradient-to-b from-[#1a1207] to-[#0A0A0A] hover:from-[#241606] text-[#D4AF37] shadow-[inset_2px_0_0_#D4AF37]'
              : 'hover:bg-[#141414] text-[#A3A3A3] hover:text-[#D4AF37] shadow-[inset_2px_0_0_#262626]'
          }`}
        >
          <div className="flex flex-col items-center gap-1.5">
            <PanelRightOpen className="w-4 h-4" />
            {counts.portalNew > 0 && (
              <span data-testid="unsched-rail-new-badge" className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
              </span>
            )}
          </div>
          <div className="text-[#D4AF37] text-[10px] font-bold tracking-[0.22em] uppercase" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
            Unscheduled · {counts.all}
          </div>
          {counts.portalNew > 0 && (
            <span
              data-testid="unsched-rail-portal-count"
              className="absolute top-1 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold uppercase tracking-wider"
            >
              {counts.portalNew} NEW
            </span>
          )}
        </button>
      ) : (
      <>
      {/* Header — aligns to the same height as the Tech/Day header row */}
      <div className="px-3 py-2 border-b border-[#262626] flex items-center justify-between gap-2">
        <div>
          <p className="text-[#D4AF37] text-[10px] font-bold tracking-[0.22em] uppercase">Unscheduled</p>
          <p className="text-white text-xs">{counts.all} item{counts.all === 1 ? '' : 's'}</p>
        </div>
        <div className="flex gap-1">
          <button
            data-testid="unsched-refresh"
            onClick={() => load()}
            className="p-1.5 rounded hover:bg-[#262626] text-[#A3A3A3] hover:text-white"
            title="Refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <Button
            data-testid="unsched-add-draft"
            onClick={() => (onAddUnscheduled ? onAddUnscheduled() : setAddDraftOpen(true))}
            size="sm"
            className="h-7 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] text-[10px] font-bold uppercase tracking-wider px-2"
          >
            <Plus className="w-3 h-3 mr-1" /> Unscheduled Job
          </Button>
          <button
            data-testid="unsched-rail-collapse"
            onClick={() => onToggleCollapsed?.(true)}
            className="p-1.5 rounded hover:bg-[#262626] text-[#A3A3A3] hover:text-white"
            title="Collapse rail"
          >
            <PanelRightClose className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Filter chips */}
      <div className="p-2 border-b border-[#262626] flex flex-wrap gap-1">
        <FilterChip value="all" label="All" count={counts.all} />
        <FilterChip value="Emergency" label="Emerg" count={counts.Emergency} />
        <FilterChip value="aging" label=">24h" count={counts.aging} />
        <FilterChip value="pm_sr" label="PM" count={counts.pm_sr} />
        <FilterChip value="customer_sr" label="Cust" count={counts.customer_sr} />
        <FilterChip value="draft" label="Draft" count={counts.draft} />
        <FilterChip value="return_visit" label="Needs Sched" count={counts.return_visit} />
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {loading ? (
          <p className="text-[#525252] text-xs">Loading…</p>
        ) : filtered.length === 0 ? (
          <p data-testid="unsched-empty" className="text-[#525252] text-xs italic">Nothing here — add a draft or wait for new requests.</p>
        ) : (
          filtered.map((item) => {
            if (item.source === 'return_visit') {
              return <ReturnVisitCard key={`rv-${item.id}`} item={item} onAddReturnVisit={onAddReturnVisit} onChanged={() => load()} />;
            }
            // iter191 — For draft (unscheduled) cards, clicking anywhere on the
            // card opens the same right-side Edit Shift drawer used by scheduled
            // jobs (via onEditUnscheduled). The Schedule button + small popup
            // remain only for pm_sr / service_request sources whose flows still
            // depend on the compact ScheduleItemDialog. This removes the
            // "small centered popup" as the primary card-click behavior.
            const isDraft = item.source === 'draft';
            const onCardClick = () => {
              if (isDraft && onEditUnscheduled) {
                onEditUnscheduled(item);
              } else {
                setScheduleItem(item);
              }
            };
            return (
            <div
              key={`${item.source}-${item.id}`}
              data-testid={`unsched-card-${item.source}-${item.id}`}
              draggable
              onDragStart={(e) => handleDragStart(e, item)}
              onDragEnd={() => { draggingRef.current = false; }}
              onClick={onCardClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onCardClick(); } }}
              className={`border-l-4 rounded-r-md p-2 cursor-pointer active:cursor-grabbing transition-colors hover:border-[#D4AF37] hover:bg-[#1a1a1a] ${PRIORITY_STYLE[item.priority] || PRIORITY_STYLE.Normal}`}
              title={isDraft ? 'Open full job drawer' : 'Schedule'}
            >
              <div className="flex items-center gap-1.5 flex-wrap mb-1">
                {item.priority === 'Emergency' && (
                  <span className="text-[#EF4444] text-[9px] font-bold uppercase tracking-wider flex items-center gap-0.5">
                    <Siren className="w-2.5 h-2.5" /> Emergency
                  </span>
                )}
                <span className={`text-[9px] font-bold uppercase tracking-wider ${item.source === 'pm_sr' ? 'text-[#D4AF37]' : item.source === 'draft' ? 'text-[#A3A3A3]' : 'text-blue-300'}`}>
                  {SOURCE_LABEL[item.source]}
                </span>
                {isAging(item.created_at) && (
                  <span className="text-[#F59E0B] text-[9px] font-bold uppercase tracking-wider flex items-center gap-0.5">
                    <Clock className="w-2.5 h-2.5" /> {ageLabel(item.created_at)}
                  </span>
                )}
              </div>
              <p className="text-white text-xs font-medium line-clamp-2">{item.title}</p>
              {(item.who || item.address) && (
                <p className="text-[#A3A3A3] text-[10px] mt-0.5 line-clamp-1">
                  {item.who}{item.unit_number ? ` — Unit ${item.unit_number}` : ''}{item.address ? ` · ${item.address}` : ''}
                </p>
              )}
              {item.customer_phone && (
                <p className="text-[#A3A3A3] text-[10px] flex items-center gap-1 mt-0.5">
                  <Phone className="w-2.5 h-2.5" /> {item.customer_phone}
                </p>
              )}
              <div className="flex items-center gap-1 mt-1.5">
                <button
                  data-testid={`unsched-schedule-${item.id}`}
                  onClick={(e) => { e.stopPropagation(); setScheduleItem(item); }}
                  className="flex-1 text-[10px] bg-[#262626] hover:bg-[#D4AF37] hover:text-[#0A0A0A] text-white rounded px-2 py-1 font-bold uppercase tracking-wider flex items-center justify-center gap-1"
                  title={isDraft ? 'Quick schedule (compact popup)' : 'Schedule'}
                >
                  <CalendarPlus className="w-3 h-3" /> {isDraft ? 'Quick Schedule' : 'Schedule'}
                </button>
                {!isDraft && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onOpenSource?.(item); }}
                    className="text-[10px] text-[#A3A3A3] hover:text-white px-1.5 py-1"
                    title="Open source"
                  >
                    <ExternalLink className="w-3 h-3" />
                  </button>
                )}
                {isDraft && (
                  <button
                    data-testid={`unsched-delete-draft-${item.id}`}
                    onClick={(e) => { e.stopPropagation(); handleDeleteDraft(item.id); }}
                    className="text-[10px] text-red-400 hover:text-red-300 px-1.5 py-1"
                    title="Delete draft"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
            );
          })
        )}
      </div>
      </>
      )}

      {/* Add Draft modal */}
      <AddDraftDialog open={addDraftOpen} onOpenChange={setAddDraftOpen} onCreated={load} />

      {/* Schedule confirmation modal (from explicit "Schedule…" button) */}
      <ScheduleItemDialog
        item={scheduleItem}
        employees={employees}
        onOpenChange={() => setScheduleItem(null)}
        onScheduled={() => { setScheduleItem(null); load(); onScheduled?.(); }}
        onOpenFull={onEditUnscheduled ? (it, qs) => { setScheduleItem(null); onEditUnscheduled({ ...it, requested_date: qs?.date || it.requested_date, requested_time: qs?.start_time || it.requested_time, requested_time_end: qs?.end_time || it.requested_time_end }); } : undefined}
      />
    </div>
  );
};


/* iter107 — Add Unscheduled Job (full intake form).
   Replaces the previous tiny "Manual Draft" modal. Captures a real job record that
   sits in the holding rail until dispatch is ready to place it on the calendar.
   The only required field is Title; everything else is optional + editable later. */
const AddDraftDialog = ({ open, onOpenChange, onCreated }) => {
  const EMPTY = {
    title: '', customer_name: '', customer_phone: '', customer_email: '',
    address: '', billing_address: '', notes: '', dispatcher_notes: '',
    priority: 'Normal', service_type: '', department: '', work_order_number: '',
    payor_type: '', hsp_info: '',
    requested_date: '', requested_time: '',
    source: '', holding_status: 'new',
  };
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(EMPTY); /* eslint-disable-next-line */ }, [open]);

  const submit = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/unscheduled-drafts`, form, { withCredentials: true });
      toast.success('Saved to Unscheduled rail');
      onCreated?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-draft-modal" className="bg-[#141414] border-[#262626] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Unscheduled Job</DialogTitle>
          <DialogDescription className="text-[#A3A3A3]">
            Stage a job in the holding area without picking a date or tech yet. Save partial info and come back when you have more.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {/* Title — only required field */}
          <div>
            <Label className="text-white text-xs">Title / Description *</Label>
            <Input data-testid="draft-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Mrs Smith — water heater leak" className="bg-[#0A0A0A] border-[#262626] text-white" />
          </div>

          {/* Customer block */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-white text-xs">Customer Name</Label>
              <Input data-testid="draft-customer-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
            <div>
              <Label className="text-white text-xs">Phone</Label>
              <Input data-testid="draft-customer-phone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
          </div>
          <div>
            <Label className="text-white text-xs">Email</Label>
            <Input data-testid="draft-customer-email" type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
          </div>

          {/* Addresses */}
          <div>
            <Label className="text-white text-xs">Service Address</Label>
            <Input data-testid="draft-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
          </div>
          <div>
            <Label className="text-white text-xs">Billing Address (if different)</Label>
            <Input data-testid="draft-billing-address" value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
          </div>

          {/* Job classification */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-white text-xs">Service Type</Label>
              <Input data-testid="draft-service-type" value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} placeholder="e.g. Drain Cleaning" className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
            <div>
              <Label className="text-white text-xs">Department</Label>
              <Select value={form.department || '__none__'} onValueChange={(v) => setForm({ ...form, department: v === '__none__' ? '' : v })}>
                <SelectTrigger data-testid="draft-department" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  <SelectItem value="__none__" className="text-white italic">— None —</SelectItem>
                  <SelectItem value="service" className="text-white">Service</SelectItem>
                  <SelectItem value="new_construction" className="text-white">New Construction</SelectItem>
                  <SelectItem value="office" className="text-white">Office</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-white text-xs">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="draft-priority" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  <SelectItem value="Normal" className="text-white">Normal</SelectItem>
                  <SelectItem value="Urgent" className="text-white">Urgent</SelectItem>
                  <SelectItem value="Emergency" className="text-white">Emergency</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white text-xs">Source (how did it come in?)</Label>
              <Select value={form.source || '__none__'} onValueChange={(v) => setForm({ ...form, source: v === '__none__' ? '' : v })}>
                <SelectTrigger data-testid="draft-source" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  <SelectItem value="__none__" className="text-white italic">— None —</SelectItem>
                  <SelectItem value="phone" className="text-white">Phone</SelectItem>
                  <SelectItem value="email" className="text-white">Email</SelectItem>
                  <SelectItem value="pm_portal" className="text-white">PM Portal</SelectItem>
                  <SelectItem value="walk_in" className="text-white">Walk-in</SelectItem>
                  <SelectItem value="website" className="text-white">Website</SelectItem>
                  <SelectItem value="text" className="text-white">Text</SelectItem>
                  <SelectItem value="other" className="text-white">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Holding-status + work order + payor */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-white text-xs">Holding Status</Label>
              <Select value={form.holding_status} onValueChange={(v) => setForm({ ...form, holding_status: v })}>
                <SelectTrigger data-testid="draft-holding-status" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  <SelectItem value="new" className="text-white">New</SelectItem>
                  <SelectItem value="needs_info" className="text-white">Needs Info</SelectItem>
                  <SelectItem value="ready_to_schedule" className="text-white">Ready to Schedule</SelectItem>
                  <SelectItem value="cancelled" className="text-white">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white text-xs">Work Order #</Label>
              <Input data-testid="draft-work-order" value={form.work_order_number} onChange={(e) => setForm({ ...form, work_order_number: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
            <div>
              <Label className="text-white text-xs">Payor Type</Label>
              <Input data-testid="draft-payor-type" value={form.payor_type} onChange={(e) => setForm({ ...form, payor_type: e.target.value })} placeholder="Cash / Insurance / HSP / Warranty" className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
          </div>

          {/* HSP / warranty info */}
          <div>
            <Label className="text-white text-xs">HSP / Warranty Info</Label>
            <Input data-testid="draft-hsp-info" value={form.hsp_info} onChange={(e) => setForm({ ...form, hsp_info: e.target.value })} placeholder="HSP claim #, warranty company, authorization, etc." className="bg-[#0A0A0A] border-[#262626] text-white" />
          </div>

          {/* Customer-requested date/time (NOT the actual schedule yet) */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-white text-xs">Requested Date (customer)</Label>
              <Input data-testid="draft-requested-date" type="date" value={form.requested_date} onChange={(e) => setForm({ ...form, requested_date: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
            <div>
              <Label className="text-white text-xs">Requested Time (customer)</Label>
              <Input data-testid="draft-requested-time" type="time" value={form.requested_time} onChange={(e) => setForm({ ...form, requested_time: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-white text-xs">Job Notes (visible to assigned tech)</Label>
            <Textarea data-testid="draft-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="bg-[#0A0A0A] border-[#262626] text-white" />
          </div>
          <div>
            <Label className="text-white text-xs">Dispatcher Notes (internal only)</Label>
            <Textarea data-testid="draft-dispatcher-notes" value={form.dispatcher_notes} onChange={(e) => setForm({ ...form, dispatcher_notes: e.target.value })} rows={2} className="bg-[#0A0A0A] border-[#262626] text-white" />
          </div>

          <div className="flex gap-2 pt-2">
            <Button data-testid="draft-save" onClick={submit} disabled={saving} className="flex-1 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">{saving ? 'Saving…' : 'Save to Unscheduled rail'}</Button>
            <Button onClick={() => onOpenChange(false)} variant="outline" className="border-[#262626] text-white hover:bg-[#262626]">Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};


/* ------------ Schedule an item (tech + date + window) ------------ */
export const ScheduleItemDialog = ({ item, employees, preset, onOpenChange, onScheduled, onOpenFull }) => {
  const [form, setForm] = useState({ employee_id: '', date: '', start_time: '09:00', end_time: '11:00' });
  const [saving, setSaving] = useState(false);
  const [needsFull, setNeedsFull] = useState('');

  useEffect(() => {
    setNeedsFull('');
    if (item) {
      // iter108 — Prefill from the draft's "requested" capture so dispatchers don't retype.
      const reqDate = item.requested_date || '';
      const reqStart = item.requested_time || '';
      const reqEnd = item.requested_time_end || '';
      setForm({
        employee_id: preset?.employee_id || '',
        date:        preset?.date || reqDate || new Date().toISOString().slice(0, 10),
        start_time:  preset?.start_time || reqStart || '09:00',
        end_time:    preset?.end_time || reqEnd || '11:00',
      });
    }
  }, [item, preset]);

  const submit = async () => {
    if (!item) return;
    if (!form.date || !form.start_time || !form.end_time) { toast.error('Date and time required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/unscheduled-work/schedule`, {
        source: item.source,
        source_id: item.id,
        employee_id: form.employee_id || null,
        date: form.date,
        start_time: form.start_time,
        end_time: form.end_time,
      }, { withCredentials: true });
      toast.success('Scheduled — appointment created');
      onScheduled?.();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to schedule';
      // Missing / unmapped Trade → Division → Service Type: route to the full Schedule Now form.
      if (item.source === 'draft' && /^To schedule this Unscheduled Job/.test(String(msg))) setNeedsFull(String(msg));
      else toast.error(msg);
    }
    setSaving(false);
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onOpenChange?.()}>
      <DialogContent data-testid="schedule-item-modal" className="bg-[#141414] border-[#262626] text-white max-w-md">
        <DialogHeader>
          <DialogTitle>Schedule: {item?.title?.slice(0, 60) || ''}</DialogTitle>
          <DialogDescription className="text-[#A3A3A3]">Assign a technician (optional) and pick a date and time window. The item will be removed from the Unscheduled rail and added to the schedule.</DialogDescription>
        </DialogHeader>
        {item && (
          <div className="space-y-3">
            <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-2.5 text-xs space-y-0.5">
              <p className="text-[#A3A3A3]">{SOURCE_LABEL[item.source]} · {item.priority}</p>
              {item.who && <p className="text-white">{item.who}{item.unit_number ? ` — Unit ${item.unit_number}` : ''}</p>}
              {item.address && <p className="text-[#A3A3A3] flex items-center gap-1"><Building2 className="w-3 h-3" /> {item.address}</p>}
              {item.customer_phone && <p className="text-[#A3A3A3] flex items-center gap-1"><Phone className="w-3 h-3" /> {item.customer_phone}</p>}
            </div>
            <div>
              <Label className="text-white text-xs">Assign Tech</Label>
              <Select value={form.employee_id || 'unassigned'} onValueChange={(v) => setForm({ ...form, employee_id: v === 'unassigned' ? '' : v })}>
                <SelectTrigger data-testid="schedule-modal-emp" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626] max-h-64">
                  <SelectItem value="unassigned" className="text-white italic">— Unassigned —</SelectItem>
                  {(employees || []).map((e) => (
                    <SelectItem key={e.id} value={e.id} className="text-white">{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-white text-xs">Date</Label>
                <Input data-testid="schedule-modal-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
              <div>
                <Label className="text-white text-xs">Start</Label>
                <Input data-testid="schedule-modal-start" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
              <div>
                <Label className="text-white text-xs">End</Label>
                <Input data-testid="schedule-modal-end" type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
            </div>
            {needsFull && (
              <div data-testid="schedule-modal-needs-full" className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200 space-y-2">
                <p>{needsFull}</p>
                {onOpenFull && (
                  <Button data-testid="schedule-modal-open-full" size="sm" onClick={() => onOpenFull(item, form)} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] h-8">
                    Open full form (Schedule Now)
                  </Button>
                )}
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <Button data-testid="schedule-modal-save" onClick={submit} disabled={saving} className="flex-1 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">{saving ? 'Scheduling…' : 'Schedule'}</Button>
              <Button onClick={() => onOpenChange?.()} variant="outline" className="border-[#262626] text-white hover:bg-[#262626]">Cancel</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UnscheduledRail;
